import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Zap, LayoutDashboard, Receipt, Gauge, MessageSquare, LogOut, Building2, User } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const items = [
  { to: "/portal", label: "Oversikt", icon: LayoutDashboard, testid: "portal-nav-oversikt", end: true },
  { to: "/portal/forbruk", label: "Forbruk", icon: Gauge, testid: "portal-nav-forbruk" },
  { to: "/portal/fakturaer", label: "Fakturaer", icon: Receipt, testid: "portal-nav-fakturaer" },
  { to: "/portal/saker", label: "Saker", icon: MessageSquare, testid: "portal-nav-saker" },
];

export default function PortalShell() {
  const { user, tenant, logout } = useAuth();

  return (
    <div className="h-screen w-screen overflow-hidden bg-[#050505] text-zinc-100 flex" data-testid="portal-shell">
      <aside className="w-64 shrink-0 border-r border-zinc-900 bg-[#080808] flex flex-col">
        <div className="h-14 px-5 flex items-center gap-2.5 border-b border-zinc-900">
          <div className="h-7 w-7 rounded-md bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center">
            <Zap className="h-4 w-4 text-black" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading font-semibold tracking-tight text-zinc-50 leading-tight">Krafthub</div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 -mt-0.5">Bedriftsportal</div>
          </div>
        </div>
        <nav className="flex-1 py-3 px-2">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.testid}
              className={({ isActive }) =>
                [
                  "relative flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 mb-0.5",
                  isActive ? "bg-zinc-900/60 text-emerald-400 nav-active" : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100",
                ].join(" ")
              }>
              <it.icon className="h-4 w-4 shrink-0" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-3 border-t border-zinc-900 text-[10px] text-zinc-600 uppercase tracking-widest">
          Selvbetjening for bedrift
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-14 shrink-0 border-b border-zinc-900 bg-[#080808]/90 backdrop-blur-md flex items-center px-5 gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Building2 className="h-4 w-4 text-emerald-400" />
            <span className="text-zinc-100 font-medium">Leverandør:</span>
            <span className="text-zinc-400">{tenant?.name}</span>
          </div>
          <div className="ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-zinc-900" data-testid="portal-user-menu">
                  <div className="h-7 w-7 rounded-full bg-gradient-to-br from-emerald-500/30 to-blue-500/30 border border-zinc-800 grid place-items-center text-xs text-emerald-300">
                    {user?.full_name?.[0] || "U"}
                  </div>
                  <div className="text-left leading-tight hidden md:block">
                    <div className="text-xs text-zinc-100 font-medium">{user?.full_name}</div>
                    <div className="text-[10px] text-zinc-500">Bedriftsbruker</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#0c0c0c] border-zinc-800 w-56">
                <DropdownMenuLabel className="text-zinc-500">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem className="text-red-400 focus:bg-red-500/10 focus:text-red-300" onClick={logout} data-testid="portal-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Logg ut
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 lg:p-8 max-w-[1400px]">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
